import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'

import type {
    Movement,
    MovementTotals,
    Product,
    Settings
} from '@/types'

import Link from 'next/link'

import {
    ArrowRight,
    ChevronLeft,
    ChevronRight,
    History,
    Pencil,
    SlidersHorizontal,
    Trash2
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Button, buttonVariants } from '@/components/ui/button'
import { getStockStatus, pluralize } from '@/lib/format'
import { useSettings } from '@/components/generals/settings-provider'
import { MOVEMENT_ICONS } from '@/components/inventory/movement-type-badge'
import { describeError } from '@/lib/db'
import { getProduct, getProductHistory, getSettings } from '@/lib/inventory/queries'
import { toCatalogProduct, useInventoryActions } from '@/components/inventory/inventory-actions'
import StockStatusBadge from '@/components/inventory/stock-status-badge'
import CategoryBadge from '@/components/inventory/category-badge'
import MovementList from '@/components/inventory/movement-list'
import StockMeter from '@/components/inventory/stock-meter'
import UnitLabel from '@/components/inventory/unit-label'
import SectionCard from '@/components/generals/section-card'
import EmptyState from '@/components/generals/empty-state'
import LoadError from '@/components/generals/load-error'
import DateTime from '@/components/generals/date-time'
import { DEFAULT_SETTINGS, PRODUCT_HISTORY_LIMIT } from '@/constants/inventory'

type ProductData = {
    product: Product
    movements: Movement[]
    totals: MovementTotals
}

interface ProductPageProps {
    settings: Settings
    data: ProductData | null
}

interface DetailRowProps {
    label: string
    children: ReactNode
}

const DetailRow: FC<DetailRowProps> = ({ label, children }): ReactNode => {
    return (
        <div className='flex items-start justify-between gap-4 py-2.5'>
            <dt className='shrink-0 text-sm text-muted-foreground'>
                {label}
            </dt>

            <dd className='min-w-0 text-right text-sm font-medium text-foreground'>
                {children}
            </dd>
        </div>
    )
}

const EmptyValue: FC<{ children: ReactNode }> = ({ children }): ReactNode => (
    <span className='font-normal text-muted-foreground'>{children}</span>
)

const ProductPage: FC<ProductPageProps> = ({ data }): ReactNode => {
    if (!data) {
        return (
            <Layout>
                <Seo title='Producto' />

                <div className='flex flex-col gap-6'>
                    <Link href='/inventario' className='inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground'>
                        <ChevronLeft className='size-4' aria-hidden='true' />
                        Volver al inventario
                    </Link>

                    <LoadError title='No se pudo cargar este producto' />
                </div>
            </Layout>
        )
    }

    return <ProductDetail {...data} />
}

const ProductDetail: FC<ProductData> = ({ product, movements, totals }): ReactNode => {
    const { money, number, percent } = useSettings()
    const { openMovement, openEditProduct, openDeleteProduct } = useInventoryActions()

    const status = getStockStatus(product.quantity, product.minStock)
    const catalogProduct = toCatalogProduct(product)
    const stockValue = product.quantity * product.cost
    const saleValue = product.quantity * product.price
    const margin = product.price - product.cost
    const marginPercent = product.price > 0 ? (margin / product.price) * 100 : null
    const gap = product.minStock - product.quantity
    const EntryIcon = MOVEMENT_ICONS.entrada
    const ExitIcon = MOVEMENT_ICONS.salida

    return (
        <Layout>
            <Seo
                title={product.name}
                description={`Stock, valor e historial de movimientos de ${product.name}.`}
            />

            <div className='flex flex-col gap-6'>
                <div className='flex flex-col gap-4'>
                    <nav aria-label='Ruta de navegación'>
                        <ol className='flex items-center gap-1.5 text-sm text-muted-foreground'>
                            <li>
                                <Link href='/inventario' className='hover:text-foreground hover:underline underline-offset-4'>
                                    Inventario
                                </Link>
                            </li>

                            <li aria-hidden='true'>
                                <ChevronRight className='size-3.5' />
                            </li>

                            <li className='min-w-0 truncate font-medium text-foreground' aria-current='page'>
                                <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                            </li>
                        </ol>
                    </nav>

                    <div className='flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between'>
                        <div className='min-w-0'>
                            <h1 className='font-heading text-2xl font-semibold tracking-tight text-balance sm:text-[1.75rem]'>
                                <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                            </h1>

                            <div className='mt-2.5 flex flex-wrap items-center gap-2'>
                                <StockStatusBadge status={status} />
                                <CategoryBadge id={product.categoryId} name={product.categoryName} color={product.categoryColor} />

                                {product.sku && (
                                    <span className='inline-flex h-6 items-center rounded-full bg-muted px-2 font-mono text-xs text-muted-foreground'>
                                        <span data-mdlfy-cms={`products:${product.id}:sku`}>{product.sku}</span>
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className='flex flex-wrap items-center gap-2'>
                            <Button onClick={() => openMovement({ product: catalogProduct, type: 'entrada' })}>
                                <EntryIcon aria-hidden='true' />
                                Entrada
                            </Button>

                            <Button
                                variant='outline'
                                disabled={product.quantity <= 0}
                                onClick={() => openMovement({ product: catalogProduct, type: 'salida' })}
                            >
                                <ExitIcon aria-hidden='true' />
                                Salida
                            </Button>

                            <Button variant='outline' onClick={() => openMovement({ product: catalogProduct, type: 'ajuste' })}>
                                <SlidersHorizontal aria-hidden='true' />
                                Ajuste
                            </Button>

                            <Button variant='outline' onClick={() => openEditProduct(product)}>
                                <Pencil aria-hidden='true' />
                                Editar
                            </Button>

                            <Button
                                variant='ghost'
                                onClick={() => openDeleteProduct({ id: product.id, name: product.name }, { redirectTo: '/inventario' })}
                                className='text-destructive hover:bg-destructive/10 hover:text-destructive'
                            >
                                <Trash2 aria-hidden='true' />
                                Eliminar
                            </Button>
                        </div>
                    </div>
                </div>

                <div className='grid gap-4 lg:grid-cols-3'>
                    <SectionCard title='Stock'>
                        <p className='font-heading text-4xl font-semibold tracking-tight tabular-nums'>
                            <span>{number(product.quantity)}</span>
                            {' '}
                            <UnitLabel productId={product.id} unit={product.unit} className='text-lg font-medium text-muted-foreground' />
                        </p>

                        <StockMeter quantity={product.quantity} minStock={product.minStock} className='mt-4 h-2' />

                        <div className='mt-3 flex items-center justify-between gap-3 text-sm'>
                            <span className='text-muted-foreground'>
                                Mínimo: <span className='font-medium text-foreground tabular-nums'>{number(product.minStock)}</span>
                            </span>

                            <span
                                className={cn(
                                    'font-medium tabular-nums',
                                    status === 'out' && 'text-danger-ink',
                                    status === 'low' && 'text-warning-ink',
                                    status === 'ok' && 'text-success-ink'
                                )}
                            >
                                {status === 'ok' && product.minStock > 0 && `${number(product.quantity - product.minStock)} por encima`}
                                {status === 'ok' && product.minStock === 0 && 'Sin mínimo definido'}
                                {status !== 'ok' && gap > 0 && `Faltan ${number(gap)}`}
                                {status !== 'ok' && gap <= 0 && (status === 'out' ? 'Sin existencias' : 'En el mínimo')}
                            </span>
                        </div>
                    </SectionCard>

                    <SectionCard title='Valor'>
                        <dl className='-my-2.5 divide-y'>
                            <DetailRow label='Valor en stock'>
                                <span className='tabular-nums'>{money(stockValue)}</span>
                            </DetailRow>

                            <DetailRow label='Precio de compra'>
                                <span className='tabular-nums'>{money(product.cost)}</span>
                            </DetailRow>

                            <DetailRow label='Precio de venta'>
                                {product.price > 0 ? <span className='tabular-nums'>{money(product.price)}</span> : <EmptyValue>Sin precio</EmptyValue>}
                            </DetailRow>

                            <DetailRow label='Margen por unidad'>
                                {marginPercent !== null ? (
                                    <span className={cn('tabular-nums', margin < 0 && 'text-danger-ink')}>
                                        {money(margin)} <span className='font-normal text-muted-foreground'>({percent(marginPercent)})</span>
                                    </span>
                                ) : <EmptyValue>Sin precio de venta</EmptyValue>}
                            </DetailRow>

                            <DetailRow label='Valor de venta del stock'>
                                {product.price > 0 ? <span className='tabular-nums'>{money(saleValue)}</span> : <EmptyValue>Sin precio</EmptyValue>}
                            </DetailRow>
                        </dl>
                    </SectionCard>

                    <SectionCard title='Detalles'>
                        <dl className='-my-2.5 divide-y'>
                            <DetailRow label='Unidad'>
                                <UnitLabel productId={product.id} unit={product.unit} />
                            </DetailRow>

                            <DetailRow label='Ubicación'>
                                {product.location
                                    ? <span data-mdlfy-cms={`products:${product.id}:location`}>{product.location}</span>
                                    : <EmptyValue>Sin ubicación</EmptyValue>}
                            </DetailRow>

                            <DetailRow label='Proveedor'>
                                {product.supplier
                                    ? <span data-mdlfy-cms={`products:${product.id}:supplier`}>{product.supplier}</span>
                                    : <EmptyValue>Sin proveedor</EmptyValue>}
                            </DetailRow>

                            <DetailRow label='Dado de alta'>
                                <DateTime value={product.createdAt} variant='date' />
                            </DetailRow>

                            <DetailRow label='Última actualización'>
                                <DateTime value={product.updatedAt} variant='relative' />
                            </DetailRow>
                        </dl>
                    </SectionCard>
                </div>

                <SectionCard
                    title='Historial de movimientos'
                    description={totals.count
                        ? `${number(totals.count)} ${pluralize(totals.count, 'movimiento', 'movimientos')} en total. Han entrado ${number(totals.unitsIn)} y han salido ${number(totals.unitsOut)}.`
                        : 'Aún no hay movimientos para este producto.'}
                    bodyClassName='p-0 sm:p-0'
                    action={totals.count > PRODUCT_HISTORY_LIMIT ? (
                        <Link
                            href={{ pathname: '/movimientos', query: { producto: product.id } }}
                            className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                        >
                            Ver todo
                            <ArrowRight aria-hidden='true' />
                        </Link>
                    ) : undefined}
                >
                    {movements.length ? (
                        <MovementList movements={movements} showProduct={false} caption={`Movimientos de ${product.name}`} />
                    ) : (
                        <EmptyState
                            icon={History}
                            title='Sin movimientos todavía'
                            description='Registra una entrada para empezar el historial de este producto.'
                            action={(
                                <Button onClick={() => openMovement({ product: catalogProduct, type: 'entrada' })}>
                                    <EntryIcon aria-hidden='true' />
                                    Registrar entrada
                                </Button>
                            )}
                            className='py-12'
                        />
                    )}
                </SectionCard>
            </div>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<ProductPageProps> = async ({ params }) => {
    const id = typeof params?.id === 'string' ? params.id : ''

    if (!id) return { notFound: true }

    try {
        const [settings, product, history] = await Promise.all([getSettings(), getProduct(id), getProductHistory(id)])

        if (!product) return { notFound: true }

        return { props: { settings, data: { product, ...history } } }
    }
    catch (error) {
        console.error('[inventario/id] No se pudo cargar el producto:', describeError(error))

        return { props: { settings: DEFAULT_SETTINGS, data: null } }
    }
}

export default ProductPage
